/**
 * The palette's keys on one platform: the pair that opens it, as the trigger
 * prints them and `aria-keyshortcuts` spells them, whether a press is them, and
 * the names that platform's keyboards print on Enter and Backspace.
 */
export interface PaletteShortcut {
  keys: readonly string[];
  aria: string;
  matches: (event: KeyboardEvent) => boolean;
  enter: string;
  back: string;
}

/** K, first pressed, with nothing held but the modifier the platform's shortcut uses. */
function isFreshK(event: KeyboardEvent): boolean {
  return (
    event.key.toLowerCase() === "k" &&
    // A held key repeats about thirty times a second, which would open and
    // shut the palette as fast.
    !event.repeat &&
    !event.altKey &&
    !event.shiftKey
  );
}

const COMMAND_K: PaletteShortcut = {
  keys: ["⌘", "K"],
  aria: "Meta+K",
  matches: (event) => isFreshK(event) && event.metaKey && !event.ctrlKey,
  enter: "Return",
  back: "Delete",
};

const CONTROL_K: PaletteShortcut = {
  keys: ["Ctrl", "K"],
  aria: "Control+K",
  matches: (event) => isFreshK(event) && event.ctrlKey && !event.metaKey,
  enter: "Enter",
  back: "Backspace",
};

/**
 * Command and K on a Mac, Control and K elsewhere, and only the platform's own.
 * On a Mac, Control and K in a text field deletes to the end of the line, which
 * the palette must not take away.
 */
export function paletteShortcutFor(
  agent: string = navigator.userAgent,
): PaletteShortcut {
  return /Mac|iPhone|iPad/.test(agent) ? COMMAND_K : CONTROL_K;
}
